/**
 * The pieces the category and manufacturer landing pages are built from.
 *
 * Both pages are the same thing with a different title and a different filter — the site's
 * PageHero, the catalogue's machine grid and a strip of links onwards — so the parts below the
 * hero live here once and each route supplies its own data and <Helmet>.
 *
 * Everything renders from props on the first pass, with no effects, because these pages are
 * server-rendered: an effect would leave crawlers with an empty page.
 */

import { useContext } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MachineContext } from "../context/MachineContext";
import { useLocalePath } from "../hooks/useLocalePath";
import { isNewMachine } from "../seoUtils";
import { categoryUrlSlug } from "../slug.js";
import ContentText from "./contentText.jsx";
import { MachinePreview } from "./machinePreview";
import Pagination from "./pagination";
import SectionContent from "./sectionContent.jsx";

export const MAX_ITEMS_PER_PAGE = 12;

/** Cap the ItemList so a big category doesn't bloat the page head. */
export const MAX_SCHEMA_ITEMS = 50;

/**
 * What is actually in stock, counted from the machines on the page rather than asserted.
 *
 * A fixed "nya och begagnade" subtitle was a claim the catalogue did not back up — every
 * machine listed today is new — so the line follows the data: all new, all used, or the split.
 * isNewMachine() decides "new" in every locale (the API translates the condition label).
 */
function StockSummary({ machines }) {
  const count = machines.length;
  if (count === 0) {
    return (
      <ContentText contentKey="landing.empty_heading" fallback="Inga maskiner i lager just nu" />
    );
  }

  const newCount = machines.filter(isNewMachine).length;
  const usedCount = count - newCount;
  const one = count === 1;

  if (usedCount === 0) {
    return (
      <ContentText
        contentKey={one ? "landing.stock_all_new_one" : "landing.stock_all_new"}
        fallback={one ? "{count} ny maskin" : "{count} nya maskiner"}
        replace={{ "{count}": count }}
      />
    );
  }

  if (newCount === 0) {
    return (
      <ContentText
        contentKey={one ? "landing.stock_all_used_one" : "landing.stock_all_used"}
        fallback={one ? "{count} begagnad maskin" : "{count} begagnade maskiner"}
        replace={{ "{count}": count }}
      />
    );
  }

  return (
    <ContentText
      contentKey="landing.stock_mixed"
      fallback="{count} maskiner · {new} nya, {used} begagnade"
      replace={{ "{count}": count, "{new}": newCount, "{used}": usedCount }}
    />
  );
}

/**
 * The catalogue's grid, paginated from `?page=` so every page is a real, linkable URL.
 * No filter controls: the whole point of a landing page is that the filter is the URL.
 */
export function MachineGrid({ machines, emptyFor }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { localePath } = useLocalePath();

  const totalPageCount = Math.ceil(machines.length / MAX_ITEMS_PER_PAGE) || 1;
  const currentPage = Math.min(
    Math.max(1, Number.parseInt(searchParams.get("page"), 10) || 1),
    totalPageCount
  );
  const paginatedItems = machines.slice(
    (currentPage - 1) * MAX_ITEMS_PER_PAGE,
    currentPage * MAX_ITEMS_PER_PAGE
  );

  const handlePageChange = (pageNumber) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", pageNumber.toString());
    setSearchParams(newParams);
    window.scrollTo(0, 0);
  };

  if (machines.length === 0) {
    return (
      <section className="py-8 md:py-12">
        <SectionContent>
          <p className="text-text-body" data-testid="collection-empty">
            <ContentText
              contentKey="landing.empty_body"
              fallback="Just nu har vi inga {category} i lager — hör av dig så letar vi upp rätt maskin åt dig."
              replace={{ "{category}": emptyFor }}
            />{" "}
            <Link to={localePath("/catalogue")} className="text-primary underline">
              <ContentText contentKey="landing.view_all" fallback="Se hela katalogen" />
            </Link>
            .
          </p>
        </SectionContent>
      </section>
    );
  }

  return (
    <section className="py-8 md:py-12 bg-surface-alt">
      <SectionContent>
        <p className="mb-4 sm:text-xl" data-testid="collection-count">
          <StockSummary machines={machines} />
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 w-full">
          {paginatedItems.map((machine) => (
            <MachinePreview machine={machine} key={machine.id} sx="h-full" />
          ))}
        </div>

        {machines.length > MAX_ITEMS_PER_PAGE && (
          <Pagination
            currentPage={currentPage}
            totalPageCount={totalPageCount}
            onPageChange={handlePageChange}
            sx="mt-4 sm:mt-8"
          />
        )}
      </SectionContent>
    </section>
  );
}

/**
 * "Andra kategorier" — the home page's tiles, minus the category the visitor is already on.
 * Only categories that actually have machines: an empty tile is a dead end.
 */
export function CategoryTiles({
  headingKey = "landing.other_categories_heading",
  headingFallback = "Andra kategorier",
  excludeSlug,
}) {
  const { categories, machineCategories } = useContext(MachineContext);
  const { localePath } = useLocalePath();

  const list = (categories?.length ? categories : machineCategories).filter(
    (category) => category.amountOfRelatedMachines > 0 && category.slug !== excludeSlug
  );

  if (list.length === 0) return null;

  return (
    // Same grey surface the home page's product catalogue sits on — the tiles are white, so on
    // white they lose their edges. The top border keeps it from merging with the machine grid,
    // which uses the same background.
    <section className="py-12 md:py-16 bg-surface-alt border-t border-border-subtle">
      <SectionContent>
        <h2 className="text-3xl md:text-4xl font-bold mb-8">
          <ContentText contentKey={headingKey} fallback={headingFallback} />
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {list.map((category) => (
            <Link
              key={category.slug}
              to={localePath(`/catalogue/${categoryUrlSlug(category)}`)}
              className="group block overflow-hidden shadow-sm hover:shadow-md transition-shadow"
              data-testid={`category-tile-${category.slug}`}
            >
              <div
                className="aspect-[3/2] bg-contain bg-center bg-no-repeat bg-white w-full"
                style={
                  category.imageUrl ? { backgroundImage: `url(${category.imageUrl})` } : undefined
                }
              />
              <div className="bg-white px-3 py-2">
                <h3 className="text-sm font-semibold text-text-strong truncate">{category.name}</h3>
              </div>
            </Link>
          ))}
        </div>
      </SectionContent>
    </section>
  );
}

/**
 * Breadcrumb structured data shared by both landing pages: Hem → Maskiner → this page.
 *
 * @param {(path: string) => string} absolute locale-aware absolute URL builder
 */
export function breadcrumbJsonLd(absolute, { name, path }) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Hem", item: absolute("/") },
      { "@type": "ListItem", position: 2, name: "Maskiner", item: absolute("/catalogue") },
      { "@type": "ListItem", position: 3, name, item: absolute(path) },
    ],
  };
}

/** ItemList of machines for the CollectionPage schema. */
export function itemListJsonLd(absolute, machines) {
  return machines.slice(0, MAX_SCHEMA_ITEMS).map((machine, index) => ({
    "@type": "ListItem",
    position: index + 1,
    url: absolute(`/machines/${machine.id}`),
    name: machine.title,
  }));
}
